import { useEffect, useSyncExternalStore } from 'react';
import type { AssetMeta } from '@cal/engine';
import { post } from './api';

export interface AssetInfo extends AssetMeta { name: string; kind: 'photo' | 'logo'; bytes: number }
interface Dto { id: string; kind: 'photo' | 'logo'; name: string; mime: string; bytes: number; width: number; height: number; hasAlpha: boolean }

/** Client cache of asset metadata (pixel size, alpha) needed by the layout engine. */
class AssetStore {
  map: Record<string, AssetInfo | undefined> = {};
  private requested = new Set<string>();
  missing = new Set<string>();
  private listeners = new Set<() => void>();
  version = 0;
  subscribe = (cb: () => void) => { this.listeners.add(cb); return () => this.listeners.delete(cb); };
  private bump() { this.version++; this.map = { ...this.map }; this.listeners.forEach((l) => l()); }
  add(d: Dto) {
    this.map[d.id] = { id: d.id, width: d.width, height: d.height, hasAlpha: d.hasAlpha, mime: d.mime, name: d.name, kind: d.kind, bytes: d.bytes };
    this.requested.add(d.id);
    this.missing.delete(d.id);
    this.bump();
  }
  async ensure(ids: string[]) {
    const need = ids.filter((id) => !this.requested.has(id));
    if (!need.length) return;
    need.forEach((id) => this.requested.add(id));
    try {
      const { assets } = await post<{ assets: Dto[] }>('/api/assets/lookup', { ids: need });
      for (const a of assets) this.map[a.id] = { id: a.id, width: a.width, height: a.height, hasAlpha: a.hasAlpha, mime: a.mime, name: a.name, kind: a.kind, bytes: a.bytes };
      for (const id of need) if (!assets.some((a) => a.id === id)) this.missing.add(id);
    } catch {
      need.forEach((id) => this.requested.delete(id));
    }
    this.bump();
  }
  pending(ids: string[]) {
    return ids.some((id) => !this.map[id] && !this.missing.has(id));
  }
}
export const assetStore = new AssetStore();

export function useAssets(ids: string[]) {
  const version = useSyncExternalStore(assetStore.subscribe, () => assetStore.version);
  const key = ids.join('|');
  useEffect(() => { void assetStore.ensure(ids); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { version, map: assetStore.map, loading: assetStore.pending(ids) };
}

export const previewUrl = (id: string) => `/api/assets/${encodeURIComponent(id)}/preview`;
export const thumbUrl = (id: string) => `/api/assets/${encodeURIComponent(id)}/thumb`;
